import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, ArrowLeft, Save, Loader2, Upload, Star, CheckCircle2, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", artist: "", artist_handle: "", medium: "", year: "", image: "", price: "", edition: "", for_sale: true, buy_url: "", description: "", featured: false, status: "published" };

function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

export default function ArtManager({ role }) {
  const isAdmin = (role || "admin") === "admin";
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/art/pieces`); setPieces(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm({ ...EMPTY, status: isAdmin ? "published" : "draft" }); setEditingId(null); setError(""); setView("edit"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditingId(p.id); setError(""); setView("edit"); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
    } catch { setError("Artwork upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setError("");
    if (!form.title.trim() || !form.artist.trim()) { setError("Piece title and artist are required."); return; }
    if (!form.image) { setError("Upload an image for the piece."); return; }
    setSaving(true);
    try {
      const payload = { ...form, featured: !!form.featured, for_sale: !!form.for_sale };
      if (editingId) await axios.put(`${API}/art/pieces/${editingId}`, payload);
      else await axios.post(`${API}/art/pieces`, payload);
      await load(); setView("list");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await axios.delete(`${API}/art/pieces/${p.id}`); await load();
  };
  const approve = async (p) => {
    try { await axios.put(`${API}/art/pieces/${p.id}`, { ...p, status: "published" }); await load(); }
    catch (err) { alert(err?.response?.data?.detail || "Publish failed."); }
  };

  const inputCls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";

  if (view === "edit") {
    return (
      <div className="max-w-3xl" data-testid="art-editor">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> Back to pieces</button>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-6">{editingId ? "Edit Piece" : "New Piece"}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Piece title</p><Input data-testid="art-f-title" value={form.title} onChange={set("title")} className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Artist</p><Input data-testid="art-f-artist" value={form.artist} onChange={set("artist")} className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Instagram @handle</p><Input data-testid="art-f-ig" value={form.artist_handle} onChange={set("artist_handle")} placeholder="@artist" className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Medium</p><Input data-testid="art-f-medium" value={form.medium} onChange={set("medium")} placeholder="Oil on canvas, photo print..." className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Year</p><Input data-testid="art-f-year" value={form.year} onChange={set("year")} placeholder="2026" className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Edition</p><Input data-testid="art-f-edition" value={form.edition} onChange={set("edition")} placeholder="1 of 1, Edition of 25..." className={inputCls} /></div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Artwork image</p>
            <div className="flex gap-2 items-center">
              <Input data-testid="art-f-image" value={form.image} onChange={set("image")} placeholder="https://... or upload" className={inputCls} />
              <label data-testid="art-image-upload" className="cursor-pointer shrink-0 inline-flex items-center gap-2 px-4 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
                {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
            {form.image && <img src={mediaUrl(form.image)} alt="" className="mt-3 w-40 h-40 object-cover border border-white/10" />}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Description</p>
            <textarea data-testid="art-f-description" value={form.description} onChange={set("description")} rows={3} placeholder="Tell the story of the piece..." className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm" />
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="art-f-forsale">
            <input type="checkbox" checked={!!form.for_sale} onChange={(e) => setForm((f) => ({ ...f, for_sale: e.target.checked }))} className="w-4 h-4 accent-[#E30613]" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-neutral-300">Available For Sale</span>
          </label>

          {form.for_sale && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Price</p><Input data-testid="art-f-price" value={form.price} onChange={set("price")} placeholder="$4,200" className={inputCls} /></div>
              <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Buy link (optional — direct checkout)</p><Input data-testid="art-f-buyurl" value={form.buy_url} onChange={set("buy_url")} placeholder="https://..." className={inputCls} /></div>
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="art-f-featured">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 accent-[#E30613]" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-neutral-300 inline-flex items-center gap-2"><Star size={13} className="text-[#E30613]" /> Feature as Gallery Highlight</span>
          </label>

          <div data-testid="art-f-status">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Status</p>
            <div className="flex gap-2">
              {[["draft", "Draft"], ["published", "Published"]].map(([k, l]) => {
                const locked = k === "published" && !isAdmin;
                return <button key={k} type="button" disabled={locked} onClick={() => !locked && setForm((f) => ({ ...f, status: k }))} className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-40 ${form.status === k ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>{l}</button>;
              })}
            </div>
            {!isAdmin && <p className="text-[10px] text-neutral-600 mt-2">Editors can save drafts. Only admins publish to the live gallery.</p>}
          </div>

          {error && <p className="text-[#E30613] text-sm" data-testid="art-f-error">{error}</p>}
          <button onClick={save} disabled={saving} data-testid="art-f-save" className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {editingId ? "Update Piece" : "Add To Gallery"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="art-manager">
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-neutral-400 text-sm">{pieces.length} piece(s) · {pieces.filter((p) => p.status === "published").length} live</p>
        <button onClick={openNew} data-testid="art-new" className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors"><Plus size={15} /> New Piece</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
      ) : pieces.length === 0 ? (
        <p className="text-neutral-600 py-16 text-center border border-white/10">No pieces yet. Add the first drop.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="art-piece-rows">
          {pieces.map((p, i) => (
            <div key={p.id} className="flex items-center gap-4 p-3" data-testid={`art-row-${i}`}>
              <div className="w-12 h-12 shrink-0 border border-white/10 overflow-hidden bg-neutral-900 flex items-center justify-center">{p.image ? <img src={mediaUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <Palette size={16} className="text-neutral-700" />}</div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-bold truncate">{p.title} {p.featured && <Star size={12} className="inline text-[#E30613]" fill="#E30613" />}</p>
                <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 truncate">{p.artist}{p.price ? ` · ${p.price}` : ""}{p.for_sale ? "" : " · not for sale"}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.15em] font-bold shrink-0 ${p.status === "pending" ? "text-amber-400" : p.published !== false ? "text-[#E30613]" : "text-neutral-600"}`}>{p.status === "pending" ? "Pending" : p.published !== false ? "Live" : "Draft"}</span>
              <div className="flex items-center gap-3 shrink-0">
                {p.status === "pending" && <button onClick={() => approve(p)} data-testid={`art-approve-${i}`} title="Publish" className="text-emerald-400 hover:text-emerald-300"><CheckCircle2 size={16} /></button>}
                <button onClick={() => openEdit(p)} data-testid={`art-edit-${i}`} title="Edit" className="text-neutral-500 hover:text-white"><Pencil size={16} /></button>
                <button onClick={() => remove(p)} data-testid={`art-delete-${i}`} title="Delete" className="text-neutral-500 hover:text-[#E30613]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
